import type { Message } from '@ag-ui/client'
import { MCP_APPS_ACTIVITY_TYPE } from '../config/chat.config'
import type { ConversationActivity, ConversationMessage } from '../types/conversation'
import { isRecord } from './json'

type ActivityMessage = Extract<Message, { role: 'activity' }>
type SystemMessage = Extract<Message, { role: 'system' }>
type DeveloperMessage = Extract<Message, { role: 'developer' }>
type UserMessage = Extract<Message, { role: 'user' }>
type AssistantMessage = Extract<Message, { role: 'assistant' }>
type ToolMessage = Extract<Message, { role: 'tool' }>

function withName<T extends { name?: string }>(message: T, name: string | null): T {
  // `null → undefined` (omit the key) — MessageSchema rejects an explicit
  // `null`. Checked against `null` specifically (not mere truthiness) so a
  // genuinely empty-but-non-null stored name still round-trips faithfully
  // rather than being silently dropped.
  if (name !== null) message.name = name
  return message
}

function toAgUiMessage(message: Omit<ConversationMessage, 'seq'>): Message {
  switch (message.role) {
    case 'system': {
      const result: SystemMessage = {
        id: message.id,
        role: 'system',
        // system/developer/tool require a non-null `content`; coalesce to "".
        content: message.content ?? '',
      }
      return withName(result, message.name)
    }
    case 'developer': {
      const result: DeveloperMessage = {
        id: message.id,
        role: 'developer',
        content: message.content ?? '',
      }
      return withName(result, message.name)
    }
    case 'user': {
      const result: UserMessage = {
        id: message.id,
        role: 'user',
        content: message.content ?? '',
      }
      return withName(result, message.name)
    }
    case 'assistant': {
      const result: AssistantMessage = {
        id: message.id,
        role: 'assistant',
      }
      // A tool-call-only assistant turn has `content: null` — omit the key
      // rather than coalescing to "", since assistant content is optional.
      if (message.content !== null) result.content = message.content
      // `toolCalls` only ever appears on `assistant`.
      if (message.toolCalls !== null) result.toolCalls = message.toolCalls
      return withName(result, message.name)
    }
    case 'tool': {
      // `name` is dropped on `tool` — ToolMessageSchema has no such field.
      const result: ToolMessage = {
        id: message.id,
        role: 'tool',
        content: message.content ?? '',
        // `toolCallId` only ever appears on `tool`.
        toolCallId: message.toolCallId ?? '',
      }
      return result
    }
    default: {
      const exhaustiveCheck: never = message.role
      throw new Error(`Unsupported conversation message role: ${String(exhaustiveCheck)}`)
    }
  }
}

/**
 * Bridges persisted `ConversationMessage` rows into AG-UI `Message`s so a
 * reopened thread can continue talking to `POST /agent`. This must be
 * lossless and symmetric — `POST /agent` validates the complete causal
 * history it is sent field-by-field against the database, so dropping,
 * reordering or renaming a field here surfaces as `409 MESSAGE_ID_CONFLICT`
 * on the client's very next turn, not here.
 *
 * Activities (MCP widgets, turn metadata) are not messages server-side; they
 * arrive in a sibling array and are spliced in right after their anchor
 * message, which is where they were emitted live. Splicing them in is safe for
 * that 409 check: the AG-UI SDK drops `role: "activity"` from every request
 * (`prepareRunAgentInput`). An activity whose anchor is not in `stored` is
 * dropped. MCP widgets are tagged `restoredFromHistory` so their actions are
 * refused. `role: "reasoning"` is never persisted, and multimodal user content
 * degrades to plain text since the server only ever stores text.
 */
/** Accepts stored or shared messages; `seq` is never read. */
export function toAgUiMessages(
  stored: readonly Omit<ConversationMessage, 'seq'>[],
  activities: readonly ConversationActivity[] = [],
): Message[] {
  const activitiesByAnchor = new Map<string, ActivityMessage[]>()
  for (const activity of activities) {
    // ActivityMessageSchema requires an object `content`.
    if (!isRecord(activity.content)) continue
    const anchored = activitiesByAnchor.get(activity.afterMessageId) ?? []
    anchored.push({
      id: activity.id,
      role: 'activity',
      activityType: activity.activityType,
      content:
        activity.activityType === MCP_APPS_ACTIVITY_TYPE
          ? { ...activity.content, restoredFromHistory: true }
          : activity.content,
    })
    activitiesByAnchor.set(activity.afterMessageId, anchored)
  }

  return stored.flatMap((message) => [
    toAgUiMessage(message),
    ...(activitiesByAnchor.get(message.id) ?? []),
  ])
}
